import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import { ArrowForward, Refresh } from '@mui/icons-material'
import { FunctionComponent, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ScreenLabelsContext } from '../../../../../../context/screen-labels/screen-labels'
import FileUploadService from '../../../../../../shared/services/file-upload/file-upload.service'
import { IBillDocument } from '../../../../../../shared/services/file-upload/file-upload.model'

export const ImportSuccessStep: FunctionComponent<{
  importResult: any
  selectedDataType: string
  onStartNewImport: () => void
  uploadResult?: any
}> = ({
  importResult,
  selectedDataType,
  onStartNewImport,
  uploadResult,
}) => {
    const navigate = useNavigate()
    //Services
    const fileUploadServiceRef = useRef(new FileUploadService())
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [billDocuments, setBillDocuments] = useState<IBillDocument[]>([])

    const isBillUpload = uploadResult?.sourceType === 'BILL_DOCUMENTS'

    // For a bill upload, say how the batch actually ended up: credit notes are set aside (they
    // import nothing by design), and the rest split into bills fully imported vs bills with rows
    // still waiting to be fixed or committed.
    useEffect(() => {
      if (!isBillUpload || !uploadResult?.id) return
      let cancelled = false
      fileUploadServiceRef.current.getBillDocuments(uploadResult.id)
        .then((response) => { if (!cancelled) setBillDocuments(response.billDocuments || []) })
        .catch(() => { })
      return () => { cancelled = true }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBillUpload, uploadResult?.id])

    let billSummaryText: string | null = null
    if (isBillUpload && billDocuments.length > 0) {
      const eligible = billDocuments.filter((document) => !document.isCreditNote)
      const successful = eligible.filter(
        (document) => (document.committedRowCount || 0) > 0 && (document.outstandingRowCount || 0) === 0
      ).length
      const pending = eligible.length - successful
      const creditNotes = billDocuments.length - eligible.length
      const buckets = [
        ...(pending > 0 ? [`${pending} pending`] : []),
        ...(successful > 0 ? [`${successful} successful`] : []),
      ]
      billSummaryText = `${eligible.length} ${eligible.length === 1 ? 'bill' : 'bills'}${buckets.length > 0 ? ` (${buckets.join(', ')})` : ''}`
        + (creditNotes > 0 ? `. ${creditNotes} credit ${creditNotes === 1 ? 'note was' : 'notes were'} set aside.` : '')
    }

    const getDataTypeLabel = (dataType: string) => {
      switch (dataType) {
        case 'emissions': return 'Emissions Data'
        case 'locations': return screenLabelsContext.getLabel('location', { plural: true, capitalize: 'first' }) + ' Data'
        case 'vehicles': return screenLabelsContext.getLabel('vehicle', { plural: true, capitalize: 'first' }) + ' Data'
        case 'products': return screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' }) + ' Data'
        case 'projects': return screenLabelsContext.getLabel('project', { plural: true, capitalize: 'first' }) + ' Data'
        case 'suppliers': return screenLabelsContext.getLabel('supplier', { plural: true, capitalize: 'first' }) + ' Data'
        default: return dataType
      }
    }

    const handleViewData = () => {
      const id = uploadResult?.id || uploadResult?.fileUploadId
      const rawType = uploadResult?.details || selectedDataType
      let routeType = rawType && String(rawType).startsWith('emissions') ? 'emissions' : rawType
      if (id && routeType) {
        navigate(`/settings/data-upload/import-history/${routeType}/${id}?tabId=1`)
        return
      }
      navigate('/settings/data-upload/import-history')
    }

    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Import complete
        </Typography>
        {billSummaryText && (
          <Typography variant="body1" sx={{ mb: 2 }}>
            {billSummaryText}
          </Typography>
        )}
        {/* The wizard holds the row counts from the validation step in memory. When they are
            there, say what actually happened; when they are not (a refresh or old link lost
            them, or a linked-activity import where not every valid row commits), keep the
            generic line rather than inventing numbers. */}
        {typeof importResult?.importedRowCount === 'number' ? (
          <>
            {importResult.importedRowCount > 0 ? (
              <Alert severity="success" sx={{ mb: importResult.skippedRowCount > 0 ? 2 : 3 }}>
                {importResult.importedRowCount} {importResult.importedRowCount === 1 ? 'row' : 'rows'} of your{' '}
                {getDataTypeLabel(selectedDataType).toLowerCase() || 'data'} {importResult.importedRowCount === 1 ? 'has' : 'have'} been imported.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: importResult.skippedRowCount > 0 ? 2 : 3 }}>
                No rows were imported.
              </Alert>
            )}
            {importResult.skippedRowCount > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {importResult.skippedRowCount === 1
                  ? '1 row has errors and was skipped. You can fix and import it from Import History.'
                  : `${importResult.skippedRowCount} rows have errors and were skipped. You can fix and import them from Import History.`}
              </Alert>
            )}
          </>
        ) : (
          <Alert severity="success" sx={{ mb: 3 }}>
            Your {getDataTypeLabel(selectedDataType).toLowerCase() || 'data'} has been imported.
          </Alert>
        )}
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 4 }}>
          <Button variant="contained" size="large" startIcon={<ArrowForward />} onClick={handleViewData} sx={{ minWidth: 160 }}>
            View data
          </Button>
          <Button variant="outlined" size="large" startIcon={<Refresh />} onClick={onStartNewImport} sx={{ minWidth: 160 }}>
            Import more
          </Button>
        </Stack>
      </Box>
    )
  }







